/**
 * The process tree page's draft as its saving stands, read outside React's renders: the version the page has changed
 * to, the version a save was last accepted for, the save on its way if one is, and a save of what waits now.
 */
export interface DraftSaving {
  version: () => number;
  saved: () => number;
  flight: () => Promise<void> | null;
  save: () => Promise<void>;
}

/**
 * Every change made on the page so far kept in the draft: true once a save holding them is accepted, false once one is
 * refused (the draft changed underneath, or an error), the page saying why where it says its saving. What reads the
 * draft is asked for only then (Paint again on a step's card paints what its picture was just set to show: design study
 * 24), never after a guess at how long a save takes. A save on its way is waited for; a change made while it was on its
 * way is saved after it.
 */
export async function untilKept(d: DraftSaving): Promise<boolean> {
  const want = d.version();
  for (let i = 0; i < 20 && d.saved() < want; i++) {
    const flight = d.flight();
    if (flight) {
      await flight;
      continue;
    }
    const before = d.saved();
    await d.save();
    // Nothing kept and nothing more on its way: refused.
    if (d.saved() === before && !d.flight()) return false;
  }
  return d.saved() >= want;
}
